import { useCallback, useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { InboxRule, InboxRuleInput } from '@hub/contracts'
import { orpc } from '@/lib/orpc-client'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { RuleEditor } from './rule-editor'

export function InboxRulesPage({ projectId }: { projectId: string }) {
  const { t } = useTranslation('inbox')
  const [rules, setRules] = useState<InboxRule[] | null>(null)
  const [editing, setEditing] = useState<InboxRule | 'new' | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<InboxRule | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const res = await orpc.inbox.listRules({ projectId })
      setRules(res.rules)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    }
  }, [projectId])

  useEffect(() => {
    void load()
  }, [load])

  const toggle = async (rule: InboxRule, enabled: boolean) => {
    setRules((prev) => prev?.map((r) => (r.id === rule.id ? { ...r, enabled } : r)) ?? prev)
    try {
      await orpc.inbox.updateRule({ projectId, ruleId: rule.id, rule: { enabled } })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
      await load()
    }
  }

  const move = async (index: number, direction: -1 | 1) => {
    if (!rules) return
    const target = index + direction
    if (target < 0 || target >= rules.length) return
    const next = [...rules]
    const [item] = next.splice(index, 1)
    next.splice(target, 0, item)
    setRules(next)
    try {
      const res = await orpc.inbox.reorderRules({ projectId, ruleIds: next.map((r) => r.id) })
      setRules(res.rules)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
      await load()
    }
  }

  const save = async (input: InboxRuleInput, ruleId: string | null) => {
    if (ruleId) await orpc.inbox.updateRule({ projectId, ruleId, rule: input })
    else await orpc.inbox.createRule({ projectId, rule: input })
    toast.success(t('rules.editor.saved'))
    setEditing(null)
    await load()
  }

  const remove = async () => {
    if (!deleteTarget) return
    setBusy(true)
    try {
      await orpc.inbox.deleteRule({ projectId, ruleId: deleteTarget.id })
      setDeleteTarget(null)
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-3xl px-6 py-6">
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 h-8 gap-1.5 px-2">
          <Link to="/$projectId/inbox" params={{ projectId }} search={{}}>
            <ArrowLeft className="h-4 w-4" />
            {t('rules.back')}
          </Link>
        </Button>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold">
              <SlidersHorizontal className="h-5 w-5" />
              {t('rules.title')}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('rules.description')}</p>
          </div>
          <Button onClick={() => setEditing('new')} className="gap-1.5">
            <Plus className="h-4 w-4" />
            {t('rules.create')}
          </Button>
        </div>

        {rules === null ? (
          <p className="text-sm text-muted-foreground">{t('loading')}</p>
        ) : rules.length === 0 ? (
          <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
            {t('rules.empty')}
          </div>
        ) : (
          <ol className="space-y-2">
            {rules.map((rule, index) => (
              <li
                key={rule.id}
                className={cn(
                  'flex items-start gap-3 rounded-lg border bg-card px-4 py-3',
                  !rule.enabled && 'opacity-60'
                )}
              >
                <span className="mt-0.5 w-5 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                  {index + 1}.
                </span>
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => setEditing(rule)}
                    className="text-left text-sm font-medium hover:underline"
                  >
                    {rule.name}
                  </button>
                  <p className="text-xs text-muted-foreground">
                    {t('rules.conditionsSummary', { count: rule.conditions.length })}
                    {rule.conditions.length > 1 && (
                      <> · {rule.matchMode === 'all' ? t('rules.matchAll') : t('rules.matchAny')}</>
                    )}
                    {rule.autoArchive && <> · {t('rules.autoArchiveOn')}</>}
                  </p>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground/80">
                    {rule.prompt}
                  </p>
                </div>
                <Switch
                  checked={rule.enabled}
                  onCheckedChange={(checked) => void toggle(rule, checked)}
                  aria-label={rule.enabled ? t('rules.enabled') : t('rules.disabled')}
                />
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onClick={() => setEditing(rule)}>
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      {t('rules.edit')}
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled={index === 0} onClick={() => void move(index, -1)}>
                      <ArrowUp className="mr-2 h-3.5 w-3.5" />
                      {t('rules.moveUp')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={index === rules.length - 1}
                      onClick={() => void move(index, 1)}
                    >
                      <ArrowDown className="mr-2 h-3.5 w-3.5" />
                      {t('rules.moveDown')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setDeleteTarget(rule)}
                    >
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      {t('rules.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            ))}
          </ol>
        )}
      </div>

      <RuleEditor
        projectId={projectId}
        open={editing !== null}
        rule={editing === 'new' ? null : editing}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
        onSave={save}
      />

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('rules.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('rules.deleteDescription', { name: deleteTarget?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={busy}>
              {t('rules.editor.cancel')}
            </Button>
            <Button variant="destructive" onClick={() => void remove()} disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('rules.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
